import React, { useState, useEffect } from 'react';
import CountyField from '../components/CountyField';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Shield, ArrowLeft, Building2, Users, Globe, Calendar,
  Clock, CheckCircle, XCircle, MapPin, TrendingUp, Activity,
  Power, PowerOff, X, Plus, Send, Edit, CreditCard, CalendarPlus, Ban, Search, Mail, Phone, LifeBuoy, RefreshCw, Menu
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CompanyLogo from '../components/CompanyLogo';
import { useAuth } from '../context/AuthContext';
import { superadminApi } from '../lib/api';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import SuperadminSidebar from '../components/SuperadminSidebar';
import { format } from 'date-fns';
import { MONTHLY, ANNUAL } from '../lib/pricing';
import { roleLabel, roleBadgeClass } from '../lib/roleBadges';

const CustomerDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [editForm, setEditForm] = useState({});
  // Sales CRM notes + follow-up (17 Jul)
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // People directory search (cockpit)
  const [peopleSearch, setPeopleSearch] = useState('');
  // Break-glass 2FA reset (G5): the person being reset, and the typed confirmation.
  const [breakGlassTarget, setBreakGlassTarget] = useState(null);
  const [breakGlassConfirm, setBreakGlassConfirm] = useState('');
  const [breakGlassBusy, setBreakGlassBusy] = useState(false);

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, user]);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const res = await superadminApi.getCustomerDetail(customerId);
      setCustomer(res.data);
      setEditForm(res.data);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      toast.error('Failed to load customer details');
      navigate('/superadmin');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    const actionText = action === 'activate' ? 'activate' : 'suspend';
    if (!window.confirm(`Are you sure you want to ${actionText} this customer?`)) return;

    try {
      await superadminApi.actionTenant(customerId, action);
      toast.success(`Customer ${action}d successfully`);
      fetchCustomer();
    } catch (error) {
      console.error('Failed to action customer:', error);
      toast.error('Failed to update customer');
    }
  };

  const breakGlassReset = async () => {
    if (!breakGlassTarget) return;
    setBreakGlassBusy(true);
    try {
      await superadminApi.resetUserTwoFactor(
        customerId, breakGlassTarget.id, breakGlassConfirm);
      toast.success(
        `Two-factor reset for ${breakGlassTarget.first_name} ${breakGlassTarget.last_name}. ` +
        `They've been emailed.`);
      setBreakGlassTarget(null);
      setBreakGlassConfirm('');
      fetchCustomer();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't reset it. Please try again.");
    } finally {
      setBreakGlassBusy(false);
    }
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const res = await superadminApi.addCustomerNote(customerId, text);
      // Prepend locally — newest first, no full page refetch.
      setCustomer((c) => ({ ...c, notes: [res.data, ...(c.notes || [])] }));
      setNoteText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save the note');
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await superadminApi.deleteCustomerNote(customerId, noteId);
      setCustomer((c) => ({ ...c, notes: (c.notes || []).filter((n) => n.id !== noteId) }));
    } catch (error) {
      toast.error('Could not delete the note');
    }
  };

  const saveFollowUp = async (value) => {
    try {
      const res = await superadminApi.setCustomerFollowUp(customerId, value || null);
      setCustomer((c) => ({ ...c, follow_up_at: res.data.follow_up_at }));
      toast.success(value ? 'Follow-up date set' : 'Follow-up cleared');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update the follow-up date');
    }
  };

  const toggleDoNotContact = async () => {
    const next = !customer.do_not_contact;
    try {
      await superadminApi.setDoNotContact(customerId, next);
      setCustomer((c) => ({ ...c, do_not_contact: next }));
      toast.success(next ? 'Marked do-not-contact' : 'Do-not-contact cleared');
    } catch (error) {
      toast.error('Could not update do-not-contact');
    }
  };

  const handleExtendTrial = async (days) => {
    setActionLoading(true);
    try {
      await superadminApi.extendTrial(customerId, days);
      toast.success(`Trial extended by ${days} days`);
      setShowExtendModal(false);
      fetchCustomer();
    } catch (error) {
      console.error('Failed to extend trial:', error);
      toast.error('Failed to extend trial');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertToPaid = async () => {
    if (!window.confirm('Convert this customer to a paid subscription?')) return;

    setActionLoading(true);
    try {
      await superadminApi.convertToPaid(customerId, 'monthly');
      toast.success('Converted to paid subscription');
      fetchCustomer();
    } catch (error) {
      console.error('Failed to convert to paid:', error);
      toast.error('Failed to convert to paid');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResync = async () => {
    setActionLoading(true);
    try {
      const res = await superadminApi.resyncBilling(customerId);
      if (res.data?.ok) {
        const provider = res.data.provider === 'gocardless' ? 'GoCardless' : 'Stripe';
        toast.success(`Billing resynced from ${provider} (${res.data.found ?? 0} subscription(s) found)`);
      } else {
        const reason = res.data?.reason;
        toast.error(
          reason === 'no_stripe_customer' ? 'This customer has no Stripe account yet.'
          : reason === 'no_subscription' ? 'This customer has no subscription yet.'
          : reason === 'gocardless_not_configured' ? 'GoCardless is not configured.'
          : 'Could not resync billing.');
      }
      fetchCustomer();
    } catch (error) {
      toast.error('Could not resync billing');
    } finally {
      setActionLoading(false);
    }
  };

  const handleInvoiceLink = async (invoiceId) => {
    try {
      const res = await superadminApi.getInvoiceLink(customerId, invoiceId);
      const url = res.data?.invoice_pdf || res.data?.hosted_invoice_url;
      if (url) {
        window.open(url, '_blank', 'noopener');
      } else {
        toast.error('No Stripe PDF available for this invoice.');
      }
    } catch (error) {
      toast.error('Could not open the invoice');
    }
  };

  const handleSaveEdit = async () => {
    setActionLoading(true);
    try {
      const res = await superadminApi.updateTenant(customerId, editForm);
      // Say what the save reached (O8): their head office follows, and a region or
      // allowance change re-prices their people — a superadmin should know both.
      const r = res?.data || {};
      const extra = [
        r.head_office_updated ? 'head office updated too' : null,
        r.repriced_in_background ? 'balances being rechecked in the background'
          : (r.repriced_count ? `${r.repriced_count} ${r.repriced_count === 1 ? 'balance' : 'balances'} rechecked` : null),
      ].filter(Boolean).join(' · ');
      toast.success(extra ? `Customer details updated — ${extra}` : 'Customer details updated');
      setShowEditModal(false);
      fetchCustomer();
    } catch (error) {
      console.error('Failed to update customer:', error);
      toast.error('Failed to update customer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleContactCustomer = () => {
    if (customer.do_not_contact) return;
    if (customer.admin_email) {
      window.open(`mailto:${customer.admin_email}?subject=HolidayHive - Your Account`, '_blank');
    }
  };

  const getCompletionColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getCompletionBg = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!customer) return null;

  // ---- Derived cockpit values (all from data already in the payload) ----
  const now = new Date();
  const daysSince = (d) => (d ? Math.floor((now - new Date(d)) / 86400000) : null);
  const relTime = (d) => {
    const days = daysSince(d);
    if (days === null) return 'never';
    if (days <= 0) return 'today';
    if (days === 1) return '1 day ago';
    if (days < 30) return `${days} days ago`;
    const m = Math.floor(days / 30);
    return m === 1 ? '1 month ago' : `${m} months ago`;
  };
  const customerLength = () => {
    const days = daysSince(customer.created_at) || 0;
    if (days < 31) return `${days} day${days !== 1 ? 's' : ''}`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months !== 1 ? 's' : ''}`;
    return `${(days / 365).toFixed(1)} years`;
  };

  const employees = customer.employees || [];
  const activeUsers = employees.filter((e) => {
    const d = daysSince(e.last_login_at);
    return d !== null && d <= 30;
  }).length;
  const roleRank = (r) => (r === 'admin' ? 3 : r === 'manager' ? 2 : 1);
  const admins = employees.filter((e) => e.role === 'admin');
  const lastAdminLogin = admins
    .map((a) => a.last_login_at)
    .filter(Boolean)
    .sort()
    .slice(-1)[0];
  const contacts = [...employees]
    .sort((a, b) => roleRank(b.role) - roleRank(a.role))
    .slice(0, 6);
  const initials = (name) =>
    (name || '?').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  const peopleQuery = peopleSearch.trim().toLowerCase();
  const filteredPeople = employees.filter((e) => {
    if (!peopleQuery) return true;
    return `${e.first_name} ${e.last_name} ${e.email} ${e.department_name || ''} ${e.role}`
      .toLowerCase()
      .includes(peopleQuery);
  });

  const ticketLabel = (s) => ({ open: 'Open', in_progress: 'In progress', resolved: 'Resolved' }[s] || s);
  const ticketCls = (s) => ({
    open: 'bg-amber-500/15 text-amber-600',
    in_progress: 'bg-blue-500/15 text-blue-500',
    resolved: 'bg-green-500/15 text-green-600',
  }[s] || 'bg-secondary text-muted-foreground');

  const adoption = [
    { label: 'Employees', on: customer.has_employees },
    { label: 'Departments', on: customer.has_departments },
    { label: 'Leave types', on: customer.has_leave_types },
    { label: 'Bank holidays', on: customer.has_public_holidays },
  ];

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />

      <SuperadminSidebar
        activeSection={null}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      <div className="flex-1 min-w-0">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-card border-b border-red-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/superadmin')}
                className="text-muted-foreground"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Customers
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/superadmin" className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                <span className="font-heading font-bold text-sm text-red-500">ADMIN</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Customer Header */}
        <div className="card-hive p-6 mb-4 border-red-500/20">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <CompanyLogo
                domain={customer.domain || customer.company_website}
                size={64}
                bg={customer.is_active ? 'bg-green-500/20' : 'bg-red-500/20'}
                iconColor={customer.is_active ? 'text-green-500' : 'text-red-500'}
              />
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-heading text-2xl font-bold">{customer.company_name}</h1>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    customer.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                  }`}>
                    {customer.subscription_status.toUpperCase()}
                  </span>
                  {customer.do_not_contact && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-500 flex items-center gap-1">
                      <Ban className="w-3.5 h-3.5" /> Do not contact
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                  {customer.domain && (
                    <span className="flex items-center gap-1"><Globe className="w-4 h-4" />{customer.domain}</span>
                  )}
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{customer.country_code}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />Customer for {customerLength()}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={handleContactCustomer}
                disabled={customer.do_not_contact || !customer.admin_email}
                title={customer.do_not_contact ? 'This customer asked not to be contacted' : 'Email the admin'}
                data-testid="contact-customer-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Contact
              </Button>

              <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)} data-testid="edit-customer-btn">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={toggleDoNotContact}
                className={customer.do_not_contact ? 'border-red-500/50 text-red-500 hover:bg-red-500/10' : ''}
                title="Toggle do-not-contact"
              >
                <Ban className="w-4 h-4 mr-2" />
                {customer.do_not_contact ? 'Allow contact' : 'Do not contact'}
              </Button>

              {/* Extend Trial + Convert to Paid — trial tenants ONLY. This still
                  covers an expired-but-unconverted trial (which keeps status
                  'trial'), while paying tenants ('paid' / 'active') correctly
                  show neither — you don't extend a trial for a live customer. */}
              {customer.subscription_status === 'trial' && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowExtendModal(true)}
                    className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                    data-testid="extend-trial-btn"
                  >
                    <CalendarPlus className="w-4 h-4 mr-2" />
                    Extend Trial
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleConvertToPaid}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="convert-paid-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Convert to Paid
                  </Button>
                </>
              )}

              {/* Resync from Stripe (Phase 6 safety net): re-reads the real
                  subscription state and repairs our record if a webhook was
                  missed or the customer ended up with duplicate subscriptions. */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleResync}
                disabled={actionLoading}
                title="Re-read this customer's subscription from Stripe and fix our record"
                data-testid="resync-billing-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Resync billing
              </Button>

              {customer.is_active ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                  onClick={() => handleAction('deactivate')}
                >
                  <PowerOff className="w-4 h-4 mr-2" />
                  Suspend
                </Button>
              ) : (
                <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => handleAction('activate')}>
                  <Power className="w-4 h-4 mr-2" />
                  Activate
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid gap-3 grid-cols-2 md:grid-cols-5 mb-4">
          <div className="card-hive p-4 border-red-500/20">
            <p className="text-sm text-muted-foreground">Seats</p>
            <p className="text-3xl font-heading font-bold">{customer.total_employees}</p>
          </div>
          <div className="card-hive p-4 border-red-500/20">
            <p className="text-sm text-muted-foreground">MRR</p>
            <p className="text-3xl font-heading font-bold text-green-500">£{customer.monthly_cost}</p>
          </div>
          <div className="card-hive p-4 border-red-500/20">
            <p className="text-sm text-muted-foreground">Active users</p>
            <p className="text-3xl font-heading font-bold">
              {activeUsers}
              <span className="text-base text-muted-foreground">/{customer.total_employees}</span>
            </p>
            <p className="text-xs text-muted-foreground">last 30 days</p>
          </div>
          <div className="card-hive p-4 border-red-500/20">
            <p className="text-sm text-muted-foreground">Leave requests</p>
            <p className="text-3xl font-heading font-bold">{customer.leave_stats.total_requests}</p>
            {customer.leave_stats.pending_requests > 0 && (
              <p className="text-xs text-yellow-500">{customer.leave_stats.pending_requests} pending</p>
            )}
          </div>
          <div className="card-hive p-4 border-red-500/20">
            <p className="text-sm text-muted-foreground">Setup</p>
            <p className={`text-3xl font-heading font-bold ${getCompletionColor(customer.completion_score)}`}>
              {customer.completion_score}%
            </p>
            <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
              <div className={`h-full ${getCompletionBg(customer.completion_score)}`} style={{ width: `${customer.completion_score}%` }} />
            </div>
          </div>
        </div>

        {/* Cockpit body */}
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Left: intelligence */}
          <div className="lg:col-span-2 space-y-4">
            {/* Growth */}
            <div className="card-hive p-4 border-red-500/20">
              <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" /> Growth
              </h3>
              {(() => {
                const hist = customer.seat_history || [];
                if (hist.length === 0) {
                  return <p className="text-sm text-muted-foreground">Not enough history yet.</p>;
                }
                const maxSeats = Math.max(...hist.map((h) => h.seats), 1);
                const first = hist[0].seats;
                const last = hist[hist.length - 1].seats;
                const change = first > 0 ? Math.round(((last - first) / first) * 100) : 0;
                const label = last > first ? 'Actively growing' : last < first ? 'Shrinking' : 'Stable';
                const labelColor = last > first ? 'text-green-500' : last < first ? 'text-red-500' : 'text-muted-foreground';
                return (
                  <>
                    <div className="flex items-end gap-2 mb-2">
                      {hist.map((h, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-[10px] text-muted-foreground">{h.seats}</span>
                          <div
                            className={`w-full rounded-t ${i === hist.length - 1 ? 'bg-green-500' : 'bg-green-500/50'}`}
                            style={{ height: `${Math.max(Math.round((h.seats / maxSeats) * 72), 4)}px` }}
                          />
                          <span className="text-[10px] text-muted-foreground">{h.label}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {first} → {last} seats over {hist.length} months ·{' '}
                      <span className={labelColor}>
                        {label}{change !== 0 ? ` (${change > 0 ? '+' : ''}${change}%)` : ''}
                      </span>{' '}
                      · £{last}/month
                    </p>
                  </>
                );
              })()}
            </div>

            {/* Engagement */}
            <div className="card-hive p-4 border-red-500/20">
              <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" /> Engagement
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {activeUsers} of {customer.total_employees} logged in this month
                {lastAdminLogin ? ` · last admin login ${relTime(lastAdminLogin)}` : ' · no admin login recorded'}
              </p>
              <div className="flex flex-wrap gap-2">
                {adoption.map((a) => (
                  <span
                    key={a.label}
                    className={`text-xs px-2.5 py-1 rounded-full flex items-center gap-1 ${
                      a.on ? 'bg-green-500/15 text-green-600' : 'bg-secondary/60 text-muted-foreground'
                    }`}
                  >
                    {a.on ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {a.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Company details & contact (read-only) */}
            <div className="card-hive p-4 border-red-500/20">
              <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" /> Company details
              </h3>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground mb-1">Address</p>
                  <p className="font-medium">
                    {[customer.address_line_1, customer.address_line_2, customer.city, customer.county, customer.postcode]
                      .filter(Boolean).join(', ') || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Phone</p>
                  <p className="font-medium">{customer.company_phone || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Website</p>
                  <p className="font-medium break-words">{customer.company_website || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Industry</p>
                  <p className="font-medium">{customer.industry || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">VAT number</p>
                  <p className="font-medium">{customer.vat_number || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Company reg</p>
                  <p className="font-medium">{customer.company_reg_number || '—'}</p>
                </div>
              </div>

              <div className="border-t border-border mt-4 pt-3">
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" /> Primary admin contact
                </p>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <p className="font-medium">{customer.admin_name || '—'}</p>
                    {customer.admin_job_title && (
                      <p className="text-xs text-muted-foreground">{customer.admin_job_title}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Email</p>
                    <p className="font-medium break-words">{customer.admin_email || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> Work phone</p>
                    <p className="font-medium">{customer.admin_work_phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> Mobile</p>
                    <p className="font-medium">{customer.admin_mobile_phone || '—'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contacts */}
            <div className="card-hive p-4 border-red-500/20">
              <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" /> Key contacts
              </h3>
              <div className="space-y-2">
                {contacts.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30">
                    <div className="w-9 h-9 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500 font-bold text-xs">
                      {initials(`${c.first_name} ${c.last_name}`)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {c.first_name} {c.last_name}
                        <span className="ml-2 text-xs text-muted-foreground capitalize">{c.role}</span>
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{relTime(c.last_login_at)}</span>
                  </div>
                ))}
              </div>
              {customer.total_employees > contacts.length && (
                <p className="text-xs text-muted-foreground mt-2">
                  Showing {contacts.length} of {customer.total_employees} people.
                </p>
              )}
            </div>

            {/* Activity timeline */}
            <div className="card-hive p-4 border-red-500/20">
              <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" /> Activity
              </h3>
              {(!customer.recent_activity || customer.recent_activity.length === 0) ? (
                <p className="text-muted-foreground text-sm">No recent activity</p>
              ) : (
                <div className="space-y-3 max-h-72 overflow-auto">
                  {customer.recent_activity.slice(0, 12).map((a, i) => (
                    <div key={i} className="flex gap-3 text-sm">
                      <div className={`w-2 h-2 rounded-full mt-2 ${a.action === 'leave_request' ? 'bg-primary' : 'bg-green-500'}`} />
                      <div>
                        <p>{a.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {a.user_name ? `${a.user_name} • ` : ''}{format(new Date(a.timestamp), 'd MMM yyyy')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Support tickets */}
            <div className="card-hive p-4 border-red-500/20">
              <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                <LifeBuoy className="w-5 h-5 text-primary" /> Support tickets
              </h3>
              {(!customer.support_tickets || customer.support_tickets.length === 0) ? (
                <p className="text-muted-foreground text-sm">No tickets from this customer.</p>
              ) : (
                <div className="space-y-2">
                  {customer.support_tickets.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => navigate(`/superadmin?section=support&ticket=${t.id}`)}
                      className="w-full text-left p-2.5 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{t.subject}</p>
                        <p className="text-xs text-muted-foreground truncate">{t.opened_by_name || 'a user'}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${ticketCls(t.status)}`}>
                        {ticketLabel(t.status)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Emails sent */}
            <div className="card-hive p-4 border-red-500/20">
              <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" /> Emails sent
              </h3>
              {(!customer.emails_sent || customer.emails_sent.length === 0) ? (
                <p className="text-muted-foreground text-sm">No emails logged yet.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-auto">
                  {customer.emails_sent.map((e) => (
                    <div key={e.id} className="flex items-start justify-between gap-3 text-sm p-2 rounded-lg bg-secondary/30">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{e.subject || e.template_key || 'Email'}</p>
                        <p className="text-xs text-muted-foreground truncate">{e.recipient}</p>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <span className={`text-xs ${e.status === 'sent' ? 'text-green-500' : 'text-red-500'}`}>
                          {e.status === 'sent' ? 'Delivered' : 'Failed'}
                        </span>
                        <p className="text-[11px] text-muted-foreground">
                          {e.created_at ? format(new Date(e.created_at), 'd MMM, HH:mm') : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right rail */}
          <div className="space-y-4">
            {/* Billing — real subscription detail for support (Phase 6) */}
            <div className="card-hive p-4">
              <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" /> Billing
              </h3>
              {(() => {
                const b = customer.billing || {};
                const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB') : '—';
                const planLabel = b.plan_type === 'annual' ? `Annual (${ANNUAL}/user/yr)`
                  : b.plan_type === 'monthly' ? `Monthly (${MONTHLY}/user/mo)`
                  : (b.state === 'trial' || b.state === 'grace') ? 'Trial' : '—';
                let statusText = 'No subscription', statusCls = 'text-muted-foreground';
                if (b.state === 'active' && b.cancel_at_period_end) { statusText = 'Active — cancelling'; statusCls = 'text-amber-500'; }
                else if (b.state === 'active') { statusText = 'Active'; statusCls = 'text-green-500'; }
                else if (b.state === 'trial') { statusText = `Trial — ${b.days_left ?? '?'} day(s) left`; statusCls = 'text-primary'; }
                else if (b.state === 'grace') { statusText = `Trial ended — pausing in ${b.grace_days_left ?? 0} day(s)`; statusCls = 'text-amber-500'; }
                else if (b.state === 'past_due') { statusText = 'Payment failing (dunning)'; statusCls = 'text-red-500'; }
                else if (b.state === 'blocked') { statusText = 'Blocked — access paused'; statusCls = 'text-red-500'; }
                else if (b.state === 'closing') { statusText = 'Account closing'; statusCls = 'text-red-500'; }
                let nextLabel = 'Next billing', nextVal = fmt(b.next_billing_date);
                if (b.cancel_at_period_end) { nextLabel = 'Access ends'; nextVal = fmt(b.current_period_end || b.next_billing_date); }
                else if (b.state === 'trial') { nextLabel = 'Trial ends → 1st charge'; nextVal = fmt(customer.trial_ends_at || b.next_billing_date); }
                else if (b.state === 'grace') { nextLabel = 'Access pauses'; nextVal = fmt(b.grace_ends_at); }
                else if (b.state === 'active') { nextLabel = 'Renews'; }
                const price = b.plan_type === 'annual' ? 10 : 1;
                const est = (b.seats != null) ? `£${(b.seats * price).toFixed(2)}/${b.plan_type === 'annual' ? 'yr' : 'mo'}` : '—';
                const pm = b.payment_method === 'stripe'
                  ? (b.payment_channel === 'bacs' ? 'Direct Debit (Stripe)' : 'Card (Stripe)')
                  : b.payment_method === 'gocardless' ? 'Direct Debit (GoCardless)' : '—';
                const Row = ({ k, v, cls }) => (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">{k}</span>
                    <span className={`font-medium text-right ${cls || ''}`}>{v}</span>
                  </div>
                );
                return (
                  <div className="space-y-2 text-sm">
                    <Row k="Status" v={statusText} cls={statusCls} />
                    <Row k="Plan" v={planLabel} />
                    <Row k="Seats" v={b.seats != null ? b.seats : '—'} />
                    <Row k="Estimated" v={est} />
                    <Row k={nextLabel} v={nextVal} />
                    <Row k="Payment" v={pm} />
                    {b.stripe_customer_id && (
                      <div className="pt-2 mt-1 border-t border-border text-xs text-muted-foreground space-y-1">
                        {b.stripe_status && <div>Sub status: <span className="font-mono">{b.stripe_status}</span></div>}
                        {b.stripe_customer_id && <div className="truncate">Customer: <span className="font-mono">{b.stripe_customer_id}</span></div>}
                        {b.stripe_subscription_id && <div className="truncate">Subscription: <span className="font-mono">{b.stripe_subscription_id}</span></div>}
                      </div>
                    )}
                    {(b.gocardless_customer_id || b.gocardless_mandate_id || b.gocardless_subscription_id) && (
                      <div className="pt-2 mt-1 border-t border-border text-xs text-muted-foreground space-y-1">
                        {b.stripe_status && <div>DD status: <span className="font-mono">{b.stripe_status}</span></div>}
                        {b.gocardless_customer_id && <div className="truncate">GC customer: <span className="font-mono">{b.gocardless_customer_id}</span></div>}
                        {b.gocardless_mandate_id && <div className="truncate">DD mandate: <span className="font-mono">{b.gocardless_mandate_id}</span></div>}
                        {b.gocardless_subscription_id && <div className="truncate">DD subscription: <span className="font-mono">{b.gocardless_subscription_id}</span></div>}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Invoices — support can pull a copy for a customer (Phase 6) */}
            {Array.isArray(customer.invoices) && customer.invoices.length > 0 && (
              <div className="card-hive p-4">
                <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-primary" /> Invoices
                </h3>
                <div className="space-y-2 text-sm">
                  {customer.invoices.map((iv) => (
                    <div key={iv.id} className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{iv.invoice_number || iv.id.slice(0, 8)}</div>
                        <div className="text-xs text-muted-foreground">
                          {iv.date ? new Date(iv.date).toLocaleDateString('en-GB') : '—'} · {iv.status || '—'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="font-medium">£{Number(iv.amount ?? 0).toFixed(2)}</span>
                        {iv.has_pdf && (
                          <Button variant="outline" size="sm" onClick={() => handleInvoiceLink(iv.id)}>
                            PDF
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Leave Policy */}
            <div className="card-hive p-4 border-red-500/20">
              <h3 className="font-heading font-bold mb-4">Leave Policy</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Annual Allowance</span>
                  <span className="font-bold">{customer.default_annual_allowance || 28} days</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Leave Year Starts</span>
                  <span className="font-bold">{months[(customer.leave_year_start_month || 1) - 1]}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Holiday Region</span>
                  <span className="font-bold capitalize">
                    {customer.public_holiday_region?.replace(/-/g, ' ') || 'Not set'}
                  </span>
                </div>
              </div>
            </div>

            {/* Setup Checklist */}
            <div className="card-hive p-4 border-red-500/20">
              <h3 className="font-heading font-bold mb-4">Setup Checklist</h3>
              <div className="space-y-3 text-sm">
                {[
                  { on: customer.has_employees, label: 'Added employees' },
                  { on: customer.has_departments, label: 'Created departments' },
                  { on: customer.has_leave_types, label: 'Configured leave types' },
                  { on: customer.has_public_holidays, label: 'Imported public holidays' },
                  { on: !!customer.domain, label: 'Domain configured' },
                ].map((row) => (
                  <div key={row.label} className="flex items-center gap-3">
                    {row.on ? <CheckCircle className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5 text-red-500" />}
                    <span className={row.on ? 'text-foreground' : 'text-muted-foreground'}>{row.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Leave Statistics */}
            <div className="card-hive p-4 border-red-500/20">
              <h3 className="font-heading font-bold mb-4">Leave Statistics</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total Requests</span>
                  <span className="font-bold">{customer.leave_stats.total_requests}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Pending</span>
                  <span className="font-bold text-yellow-500">{customer.leave_stats.pending_requests}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Approved</span>
                  <span className="font-bold text-green-500">{customer.leave_stats.approved_requests}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Declined</span>
                  <span className="font-bold text-red-500">{customer.leave_stats.declined_requests}</span>
                </div>
                <div className="border-t border-border pt-3 mt-1">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Days taken this year</span>
                    <span className="font-bold">{customer.leave_stats.days_taken_this_year}</span>
                  </div>
                  {customer.leave_stats.most_common_leave_type && (
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-muted-foreground">Most used type</span>
                      <span className="font-bold text-primary">{customer.leave_stats.most_common_leave_type}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* People directory — find anyone (cockpit) */}
        <div className="card-hive p-4 mt-4 border-red-500/20">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
            <h3 className="font-heading font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" /> People ({customer.total_employees})
            </h3>
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={peopleSearch}
                onChange={(e) => setPeopleSearch(e.target.value)}
                placeholder="Search name, email, department…"
                className="input-hive pl-9"
              />
            </div>
          </div>

          {filteredPeople.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No one matches "{peopleSearch}".</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Role</th>
                    <th className="py-2 pr-3 font-medium">Email</th>
                    <th className="py-2 pr-3 font-medium">Department</th>
                    <th className="py-2 pr-3 font-medium whitespace-nowrap">Last login</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 font-medium">Two-factor</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPeople.map((e) => (
                    <tr key={e.id} className="border-b border-border/50 hover:bg-secondary/30">
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500 font-bold text-[10px]">
                            {initials(`${e.first_name} ${e.last_name}`)}
                          </div>
                          <span className="font-medium whitespace-nowrap">{e.first_name} {e.last_name}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${roleBadgeClass(e.role)}`}>{roleLabel(e.role)}</span>
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">{e.email}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{e.department_name || '—'}</td>
                      <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">{relTime(e.last_login_at)}</td>
                      <td className="py-2 pr-3">
                        {e.is_active ? (
                          <span className="text-xs text-green-500">Active</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Archived</span>
                        )}
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        {e.twofactor_enrolled ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-600">
                              {e.twofactor_method === 'totp' ? 'App' : 'Email'}
                            </span>
                            {/* Break-glass (G5): only for people who actually have it on. */}
                            <button
                              type="button"
                              onClick={() => { setBreakGlassTarget(e); setBreakGlassConfirm(''); }}
                              className="text-xs text-red-500 hover:text-red-400 underline underline-offset-2"
                              data-testid={`break-glass-${e.id}`}
                            >
                              Reset…
                            </button>
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Off</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sales notes — full width history (newest first) */}
        <div className="card-hive p-4 mt-4 border-red-500/20">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
            <h3 className="font-heading font-bold flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" /> Sales notes
            </h3>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                <CalendarPlus className="w-3.5 h-3.5" /> Next follow-up
              </label>
              <Input
                type="date"
                value={customer.follow_up_at || ''}
                onChange={(e) => saveFollowUp(e.target.value)}
                className="input-hive w-auto"
              />
              {customer.follow_up_at && (
                <Button variant="ghost" size="sm" onClick={() => saveFollowUp('')} title="Clear">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Add a note */}
          <div className="flex items-start gap-2 mb-4">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote(); }}
              placeholder="Jot down what happened… (Ctrl+Enter to save)"
              rows={2}
              className="input-hive flex-1 resize-y text-sm"
            />
            <Button size="sm" onClick={addNote} disabled={savingNote || !noteText.trim()} title="Add note">
              <Plus className="w-4 h-4 mr-2" /> Add
            </Button>
          </div>

          {/* The log */}
          {(customer.notes && customer.notes.length > 0) ? (
            <div className="space-y-2 max-h-96 overflow-auto">
              {customer.notes.map((n) => (
                <div key={n.id} className="group rounded-lg bg-muted/40 p-3 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <p className="whitespace-pre-wrap break-words flex-1">{n.content}</p>
                    <button
                      onClick={() => deleteNote(n.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-500"
                      title="Delete note"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {n.author_name || n.author_email || 'Superadmin'} · {n.created_at ? format(new Date(n.created_at), 'd MMM yyyy, HH:mm') : ''}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-3">No notes yet.</p>
          )}
        </div>
      </main>
      </div>

      {/* Extend Trial Modal */}
      {breakGlassTarget && (() => {
        // The typed confirmation is their email — or their full name for the email-less
        // employees decision H allows. The backend checks the same thing.
        const expected = breakGlassTarget.email
          || `${breakGlassTarget.first_name || ''} ${breakGlassTarget.last_name || ''}`.trim();
        const matches = breakGlassConfirm.trim().toLowerCase() === expected.toLowerCase();
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
               onClick={() => setBreakGlassTarget(null)}>
            <div className="bg-card rounded-xl max-w-md w-full border border-red-500/20"
                 onClick={(e) => e.stopPropagation()} data-testid="break-glass-dialog">
              <div className="p-6 border-b border-border flex items-center justify-between">
                <h3 className="font-heading font-bold text-lg">
                  Reset two-factor for {breakGlassTarget.first_name} {breakGlassTarget.last_name}?
                </h3>
                <button onClick={() => setBreakGlassTarget(null)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  This switches off their two-factor authentication and destroys their
                  recovery codes. They'll sign in with just their password — and if their
                  company requires two-factor for their role, they'll be made to set it up
                  fresh the moment they next sign in.
                </p>
                <p className="text-sm text-muted-foreground">We'll email them to say this happened.</p>
                <div className="border border-red-500/40 bg-red-500/5 rounded-lg p-3">
                  <p className="text-sm text-red-500">
                    Be certain who you're talking to. A caller pretending to be locked out
                    is exactly how accounts get stolen — ring them back on a number you
                    already have, not one they gave you.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground" htmlFor="break-glass-confirm">
                    Type {breakGlassTarget.email ? 'their email address' : 'their full name'} to confirm
                  </label>
                  <Input
                    id="break-glass-confirm"
                    value={breakGlassConfirm}
                    onChange={(e) => setBreakGlassConfirm(e.target.value)}
                    placeholder={expected}
                    autoFocus
                    data-testid="break-glass-confirm-input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setBreakGlassTarget(null)}
                          disabled={breakGlassBusy}>
                    Cancel
                  </Button>
                  <Button
                    className="bg-red-600 hover:bg-red-500 text-white"
                    onClick={breakGlassReset}
                    disabled={!matches || breakGlassBusy}
                    data-testid="break-glass-submit"
                  >
                    {breakGlassBusy ? 'Resetting…' : 'Reset two-factor'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showExtendModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowExtendModal(false)}>
          <div className="bg-card rounded-xl max-w-md w-full border border-red-500/20" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg">Extend Trial</h3>
              <button onClick={() => setShowExtendModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-muted-foreground">
                How many days would you like to extend the trial for <strong>{customer.company_name}</strong>?
              </p>
              <div className="grid grid-cols-3 gap-3">
                {[7, 14, 30].map((days) => (
                  <Button
                    key={days}
                    variant="outline"
                    className="h-16 flex flex-col"
                    onClick={() => handleExtendTrial(days)}
                    disabled={actionLoading}
                    data-testid={`extend-${days}-days-btn`}
                  >
                    <span className="text-2xl font-bold">{days}</span>
                    <span className="text-xs text-muted-foreground">days</span>
                  </Button>
                ))}
              </div>
              {customer.trial_ends_at && (
                <p className="text-sm text-muted-foreground">
                  Current trial expires: {format(new Date(customer.trial_ends_at), 'PPP')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-card rounded-xl max-w-2xl w-full max-h-[80vh] overflow-auto border border-red-500/20" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-card">
              <h3 className="font-heading font-bold text-lg">Edit Customer Details</h3>
              <button onClick={() => setShowEditModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-2">Company Name</label>
                  <Input
                    value={editForm.company_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Domain</label>
                  <Input
                    value={editForm.domain || ''}
                    onChange={(e) => setEditForm({ ...editForm, domain: e.target.value })}
                    placeholder="example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <Input
                    value={editForm.company_phone || ''}
                    onChange={(e) => setEditForm({ ...editForm, company_phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Website</label>
                  <Input
                    value={editForm.company_website || ''}
                    onChange={(e) => setEditForm({ ...editForm, company_website: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Industry</label>
                  <Input
                    value={editForm.industry || ''}
                    onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Annual Allowance (days)</label>
                  <Input
                    type="number"
                    value={editForm.default_annual_allowance || 28}
                    onChange={(e) => setEditForm({ ...editForm, default_annual_allowance: parseFloat(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">Address</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <Input
                      placeholder="Address Line 1"
                      value={editForm.address_line_1 || ''}
                      onChange={(e) => setEditForm({ ...editForm, address_line_1: e.target.value })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Input
                      placeholder="Address Line 2"
                      value={editForm.address_line_2 || ''}
                      onChange={(e) => setEditForm({ ...editForm, address_line_2: e.target.value })}
                    />
                  </div>
                  {/* Added 21/10/2026 (O8) — the customer's own two doors have a third line,
                      so this one must too (decision I3: the doors offer the same fields). */}
                  <div className="md:col-span-2">
                    <Input
                      placeholder="Address line 3"
                      value={editForm.address_line_3 || ''}
                      onChange={(e) => setEditForm({ ...editForm, address_line_3: e.target.value })}
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="City"
                      value={editForm.city || ''}
                      onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    />
                  </div>
                  <div>
                    {/* ⚠️ THE SHARED CONTROL — this is a CUSTOMER'S company county, the
                        same field their own Settings screen shows, so it has to offer the
                        same words. Found 13/08/2026 by the guard in lib/counties.checks.mjs
                        rather than by anyone noticing.
                        ✅ BUGS.md O8 FIXED 21/10/2026: saving here now copies the address
                        onto their head office and re-prices if the region or allowance
                        moved — exactly like their own Settings → Address. */}
                    <CountyField
                      id="customer-county"
                      countryCode={editForm.country_code || customer.country_code}
                      value={editForm.county}
                      onChange={(county) => setEditForm({ ...editForm, county })}
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Postcode"
                      value={editForm.postcode || ''}
                      onChange={(e) => setEditForm({ ...editForm, postcode: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={actionLoading}>
                  {actionLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetailPage;
